// this is my current quiz page

import React, { useEffect, useState, useRef } from "react";
import Layout from "../Layout";
import "katex/dist/katex.min.css";
import { Button, Card, Col, Progress, Row, Spin } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import "./QuizPage.css"; // Add a separate CSS file for advanced styling
import { motion, AnimatePresence } from "framer-motion";
import {
    BookOpen,
    Clock,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    XCircle,
    Award,
    RefreshCw,
    Timer,
    ArrowRight,
    CircleCheck,
    Zap,
    AlertTriangle, ArrowLeft
} from "lucide-react";
import { API_CONFIG } from "../config/api";


const QuizPage = () => {
    const [books, setBooks] = useState([]);
    const [selectedBook, setSelectedBook] = useState(null);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [feedback, setFeedback] = useState([]);
    const [scoreDetails, setscoreDetails] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const server_url = API_CONFIG.BASE_URL;
    const [quizVisible, setQuizVisible] = useState(false); // Control Quiz Modal
    // Track Attempted Questions & Animations
    const [attemptedQuestions, setAttemptedQuestions] = useState({});
    const [quizTimer, setQuizTimer] = useState(600); // 10 minutes (600 seconds)
    const [animateNext, setAnimateNext] = useState(false);
    const [quizStarted, setQuizStarted] = useState(false); // To control visibility
    const [quizEnded, setQuizEnded] = useState(false); // Track quiz completion

    const [streak, setStreak] = useState(0);

    // Growth quiz support
    const [quizFormat, setQuizFormat] = useState("legacy"); // "legacy" or "growth"
    const [growthQuizId, setGrowthQuizId] = useState(null);
    const [growthAnswers, setGrowthAnswers] = useState({}); // { index: { selected_option, text_answer, bool_answer, explanation } }
    const [pendingReviewCount, setPendingReviewCount] = useState(0);


    // Format time from seconds to MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Get progress percentage
    const getProgressPercentage = () => {
        if (!quizQuestions.length) return 0;
        return ((currentQuestionIndex + 1) / quizQuestions.length) * 100;
    };

    useEffect(() => {
        if (!quizStarted || quizQuestions.length === 0) return;  // ✅ Ensure timer starts when quiz data is ready

        const timerInterval = setInterval(() => {
            setQuizTimer(prev => {
                if (prev > 0) return prev - 1;
                clearInterval(timerInterval);
                handleSubmit(); // Auto-submit if time runs out
                return 0;
            });
        }, 1000);

        return () => clearInterval(timerInterval);
    }, [quizStarted, quizQuestions]); // ✅ Re-run when quizQuestions are set


    // Handle answer selection
    const handleAnswerChange = (questionIndex, option) => {
        setAnswers(prev => ({
            ...prev,
            [questionIndex]: option,
        }));

        setAttemptedQuestions(prev => ({
            ...prev,
            [questionIndex]: true,
        }));

        // Track streak for gamification (no visual feedback shown)
        const isCorrect = option === quizQuestions[questionIndex].correct_option;
        if (isCorrect) {
            setStreak(prev => prev + 1);
        } else {
            setStreak(0);
        }

        // Auto-advance to next question after brief pause
        setTimeout(() => nextQuestion(), 300);
    };

    const nextQuestion = () => {
        if (currentQuestionIndex < quizQuestions.length - 1) {
            setAnimateNext(true);
            setTimeout(() => {
                setCurrentQuestionIndex(prev => {
                    // If no answer is selected, mark it as skipped
                    if (!answers[prev]) {
                        setAttemptedQuestions(prevState => ({
                            ...prevState,
                            [prev]: false  // False means skipped
                        }));
                    }
                    return prev + 1;
                });
                setAnimateNext(false);
            }, 300);
        }
    };
    const prevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setAnimateNext(true);
            setTimeout(() => {
                setCurrentQuestionIndex(prev => prev - 1);
                setAnimateNext(false);
            }, 300);
        }
    };


    // const nextQuestion = () => {
    //     if (currentQuestionIndex < quizQuestions.length - 1) {
    //         setAnimateNext(true);
    //         setTimeout(() => {
    //             setCurrentQuestionIndex(prev => prev + 1);
    //             setAnimateNext(false);
    //         }, 300); // ✅ Delay for smooth transition
    //     }
    // };


    // Fetch books when component mounts
    useEffect(() => {
        const fetchBooks = async () => {
            const accessToken = localStorage.getItem("accessToken");
            try {
                const response = await fetch(`${server_url}/books`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                setBooks(data.books);
            } catch (error) {
                console.error("Error fetching books:", error);
            }
        };
        fetchBooks();
    }, [server_url]);

    // Fetch topics when a book is selected
    const fetchTopics = async (bookId) => {
        setLoading(true);
        const accessToken = localStorage.getItem("accessToken");
        try {
            const response = await fetch(`${server_url}/books/${bookId}/topics`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setTopics(data.topics);
        } catch (error) {
            console.error("Error fetching topics:", error);
        }
        setLoading(false);
    };

    // Fetch quiz questions when a topic is selected
    const fetchQuiz = async (topicId) => {
        setLoading(true);
        const accessToken = localStorage.getItem("accessToken");

        try {
            const response = await fetch(`${server_url}/quizzes/${topicId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            setQuizQuestions(data.quiz);
            setSelectedTopic(topicId);
            setCurrentQuestionIndex(0);

            // Detect growth quiz format
            const fmt = data.quiz_format || "legacy";
            setQuizFormat(fmt);
            setGrowthQuizId(data.growth_quiz_id || null);
            setGrowthAnswers({});

            // ✅ Reset all previous progress
            setAnswers({});
            setAttemptedQuestions({});
            setFeedback([]);
            setscoreDetails([]);

            // ✅ Reset the timer to 10 minutes (600 seconds)
            setQuizTimer(600);

            setQuizStarted(true);
            setQuizEnded(false);

        } catch (error) {
            console.error("Error fetching quiz:", error);
        }

        setLoading(false);
    };


    // Handle answer selection
    // const handleAnswerChange = (question, option) => {
    //     setAnswers((prev) => ({
    //         ...prev,
    //         [question]: option,
    //     }));
    // };

    // // Handle navigation
    // const nextQuestion = () => {
    //     if (currentQuestionIndex < quizQuestions.length - 1) {
    //         setCurrentQuestionIndex((prev) => prev + 1);
    //     }
    // };

    // const prevQuestion = () => {
    //     if (currentQuestionIndex > 0) {
    //         setCurrentQuestionIndex((prev) => prev - 1);
    //     }
    // };

    // Submit the quiz
    const handleSubmit = async () => {
        setIsSubmitting(true);
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
            console.error("Access token is missing.");
            return;
        }

        try {
            let response;

            if (quizFormat === "growth" && growthQuizId) {
                // Growth quiz submission — multi-type with evidence tracking
                const growthSubmission = {
                    growth_quiz_id: growthQuizId,
                    answers: quizQuestions.map((question, index) => {
                        const ga = growthAnswers[index] || {};
                        return {
                            question_index: question.question_index ?? index,
                            question_type: question.question_type || "mcq",
                            selected_option: ga.selected_option || answers[index] || null,
                            text_answer: ga.text_answer || null,
                            bool_answer: ga.bool_answer ?? null,
                            explanation: ga.explanation || null,
                        };
                    }),
                };

                response = await fetch(`${server_url}/quizzes/growth-submit`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(growthSubmission),
                });
            } else {
                // Legacy MCQ submission
                const submissionData = {
                    answers: quizQuestions.map((question, index) => ({
                        question: question.question,
                        selected_option: answers[index] ? answers[index] : "Skipped"
                    })),
                };

                response = await fetch(`${server_url}/quizzes/${selectedTopic}/submit`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(submissionData),
                });
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error submitting quiz:", response.status, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const feedbackData = await response.json();
            setQuizVisible(false);
            setIsSubmitting(false);

            if (quizFormat === "growth") {
                // Growth quizzes need teacher review — do not show instant score
                setPendingReviewCount(feedbackData.pending_review_count || 0);
            } else {
                // Legacy MCQ — results are immediate
                setFeedback(feedbackData.feedback);
                setscoreDetails(feedbackData);
            }

            setQuizEnded(true);
            setQuizStarted(false);

        } catch (error) {
            console.error("Error submitting quiz:", error);
        }
    };

    // Returns true if the question at `index` has been answered (MCQ or subjective/growth)
    const isQuestionAnswered = (index) => {
        if (answers[index] !== undefined) return true;
        const ga = growthAnswers[index];
        if (!ga) return false;
        return (
            ga.selected_option !== undefined ||
            (ga.text_answer && ga.text_answer.trim() !== '') ||
            (ga.bool_answer !== undefined && ga.bool_answer !== null)
        );
    };

    // Get emoji based on score percentage
    const getScoreEmoji = (percentage) => {
        if (percentage >= 90) return "🏆";
        if (percentage >= 80) return "🎉";
        if (percentage >= 70) return "👍";
        if (percentage >= 60) return "😊";
        if (percentage >= 50) return "🙂";
        return "📚";
    };

    // Get color based on score percentage
    const getScoreColor = (percentage) => {
        if (percentage >= 80) return "bg-gradient-to-r from-green-500 to-green-600";
        if (percentage >= 60) return "bg-gradient-to-r from-blue-500 to-blue-600";
        if (percentage >= 40) return "bg-gradient-to-r from-yellow-500 to-yellow-600";
        return "bg-gradient-to-r from-red-500 to-red-600";
    };

    // Card variants for animation
    const cardVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring",
                duration: 0.6,
                staggerChildren: 0.1
            }
        },
        exit: { opacity: 0, y: -30, scale: 0.9 }
    };

    // Button hover animation
    const buttonHover = {
        scale: 1.03,
        transition: { duration: 0.2 }
    };

    const questionRefs = useRef([]);
    useEffect(() => {
        if (questionRefs.current[currentQuestionIndex]) {
            questionRefs.current[currentQuestionIndex].scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    }, [currentQuestionIndex]);


    return (
        <Layout>
            {/*<div className="container mx-auto py-6">*/}
            {/*    /!* Show books and topics only if quiz is NOT started *!/*/}
            {/*    {!quizStarted && !quizEnded && (*/}
            {/*        <>*/}
            {/*            <h2 className="text-3xl font-semibold  font-gothamtext-[#333] dark:text-white mb-6">*/}
            {/*                Select a Quiz Book*/}
            {/*            </h2>*/}

            {/*            /!* Book Cards *!/*/}
            {/*            <Row gutter={[16, 16]}>*/}
            {/*                {books.map((book) => (*/}
            {/*                    <Col xs={24} sm={12} md={8} lg={6} key={book.book_id}>*/}
            {/*                        <Card hoverable*/}
            {/*                              className="shadow-lg rounded-xl transition-all duration-300 transform hover:scale-105">*/}
            {/*                            <Card.Meta*/}
            {/*                                title={<h3 className="text-xl font-semibold font-gotham">{book.name}</h3>}/>*/}
            {/*                            <p className="mt-4 text-gray-600">*/}
            {/*                                <strong>Hours to Complete:</strong> {book.hours_to_complete}*/}
            {/*                            </p>*/}
            {/*                            <Button*/}
            {/*                                type="primary"*/}
            {/*                                size="large"*/}
            {/*                                className="mt-4 w-full bg-yellow-400 hover:bg-yellow-600 text-black rounded-md shadow-md"*/}
            {/*                                onClick={() => {*/}
            {/*                                    setSelectedBook(book);*/}
            {/*                                    fetchTopics(book.book_id);*/}
            {/*                                }}*/}
            {/*                            >*/}
            {/*                                Select Book*/}
            {/*                            </Button>*/}
            {/*                        </Card>*/}
            {/*                    </Col>*/}
            {/*                ))}*/}
            {/*            </Row>*/}

            {/*            /!* Topics Section *!/*/}
            {/*            {selectedBook && (*/}
            {/*                <div className="mt-6">*/}
            {/*                    <h3 className="text-xl font-bold font-gotham mb-4">Select a Topic</h3>*/}
            {/*                    {loading ? (*/}
            {/*                        <Spin size="large" style={{color: "yellow-500"}}/>*/}
            {/*                    ) : (*/}
            {/*                        <Row gutter={[16, 16]}>*/}
            {/*                            {topics.map((topic) => (*/}
            {/*                                <Col xs={24} sm={12} md={8} key={topic.topic_id}>*/}
            {/*                                    <Button*/}
            {/*                                        type="default"*/}
            {/*                                        className="w-full bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"*/}
            {/*                                        onClick={() => fetchQuiz(topic.topic_id)}*/}
            {/*                                    >*/}
            {/*                                        {topic.name}*/}
            {/*                                    </Button>*/}
            {/*                                </Col>*/}
            {/*                            ))}*/}
            {/*                        </Row>*/}
            {/*                    )}*/}
            {/*                </div>*/}
            {/*            )}*/}
            {/*        </>*/}
            {/*    )}*/}

            {/*<h2 className="text-3xl font-semibold text-[#333] dark:text-white mb-6">*/}
            {/*    Select a Quiz Book*/}
            {/*</h2>*/}

            {/*/!* Book Cards *!/*/}
            {/*<Row gutter={[16, 16]}>*/}
            {/*    {books.map((book) => (*/}
            {/*        <Col xs={24} sm={12} md={8} lg={6} key={book.book_id}>*/}
            {/*            <Card hoverable*/}
            {/*                  className="shadow-lg rounded-xl transition-all duration-300 transform hover:scale-105">*/}
            {/*                <Card.Meta title={<h3 className="text-xl font-semibold">{book.name}</h3>}/>*/}
            {/*                <p className="mt-4 text-gray-600">*/}
            {/*                    <strong>Hours to Complete:</strong> {book.hours_to_complete}*/}
            {/*                </p>*/}
            {/*                <Button*/}
            {/*                    type="primary"*/}
            {/*                    size="large"*/}
            {/*                    className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md shadow-md"*/}
            {/*                    onClick={() => {*/}
            {/*                        setSelectedBook(book);*/}
            {/*                        fetchTopics(book.book_id);*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    Select Book*/}
            {/*                </Button>*/}
            {/*            </Card>*/}
            {/*        </Col>*/}
            {/*    ))}*/}
            {/*</Row>*/}

            {/*/!* Topics Section *!/*/}
            {/*{selectedBook && (*/}
            {/*    <div className="mt-6">*/}
            {/*        <h3 className="text-xl font-bold mb-4">Select a Topic</h3>*/}
            {/*        {loading ? (*/}
            {/*            <Spin size="large"/>*/}
            {/*        ) : (*/}
            {/*            <Row gutter={[16, 16]}>*/}
            {/*                {topics.map((topic) => (*/}
            {/*                    <Col xs={24} sm={12} md={8} key={topic.topic_id}>*/}
            {/*                        <Button*/}
            {/*                            type="default"*/}
            {/*                            className="w-full bg-blue-500 text-white p-2 rounded-md"*/}
            {/*                            onClick={() => fetchQuiz(topic.topic_id)}*/}
            {/*                        >*/}
            {/*                            {topic.name}*/}
            {/*                        </Button>*/}
            {/*                    </Col>*/}
            {/*                ))}*/}
            {/*            </Row>*/}
            {/*        )}*/}
            {/*    </div>*/}
            {/*)}*/}

            {/*/!* Quiz Card *!/*/}
            {/*{quizQuestions.length > 0 && (*/}
            {/*    <Card className="mt-6 shadow-lg rounded-xl border border-gray-300">*/}
            {/*        <p className="text-gray-700 font-bold mb-2 text-lg">*/}
            {/*            Question {currentQuestionIndex + 1} of {quizQuestions.length}*/}
            {/*        </p>*/}

            {/*        <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>*/}
            {/*            {quizQuestions[currentQuestionIndex].question}*/}
            {/*        </ReactMarkdown>*/}

            {/*        {quizQuestions[currentQuestionIndex].options.map((option, i) => (*/}
            {/*            <label*/}
            {/*                key={i}*/}
            {/*                className={`block px-4 py-2 my-2 text-lg border rounded-md cursor-pointer transition-all duration-200 ${*/}
            {/*                    answers[quizQuestions[currentQuestionIndex].question] === option*/}
            {/*                        ? "bg-blue-500 text-white border-blue-700"*/}
            {/*                        : "bg-gray-100 hover:bg-gray-200"*/}
            {/*                }`}*/}
            {/*                onClick={() => handleAnswerChange(quizQuestions[currentQuestionIndex].question, option)}*/}
            {/*            >*/}
            {/*                {option}*/}
            {/*            </label>*/}
            {/*        ))}*/}

            {/*        <div className="mt-4 flex justify-between">*/}
            {/*            <Button onClick={prevQuestion} disabled={currentQuestionIndex === 0}>*/}
            {/*                Back*/}
            {/*            </Button>*/}
            {/*            {currentQuestionIndex === quizQuestions.length - 1 ? (*/}
            {/*                <Button type="primary" onClick={handleSubmit} loading={isSubmitting}>*/}
            {/*                    Submit*/}
            {/*                </Button>*/}
            {/*            ) : (*/}
            {/*                <Button type="primary" onClick={nextQuestion}>*/}
            {/*                    Next*/}
            {/*                </Button>*/}
            {/*            )}*/}
            {/*        </div>*/}
            {/*    </Card>*/}
            {/*)}*/}

            {/* Question Progress Indicators */}
            {/*<div className="question-progress">*/}
            {/*    {quizQuestions.map((_, index) => (*/}
            {/*        <div*/}
            {/*            key={index}*/}
            {/*            className={`circle ${answers[index] !== undefined ? "attempted" : "unattempted"}`}*/}
            {/*        >*/}
            {/*            {index + 1}*/}
            {/*        </div>*/}
            {/*    ))}*/}
            {/*</div>*/}

            {/*<Modal*/}
            {/*    title="Quiz"*/}
            {/*    open={quizVisible}*/}
            {/*    onCancel={() => setQuizVisible(false)}*/}
            {/*    footer={null}*/}
            {/*    width={600}*/}
            {/*    centered*/}
            {/*>*/}
            {/*    {quizQuestions.length > 0 && (*/}
            {/*        <div className="quiz-timer">*/}
            {/*            Time*/}
            {/*            Left: <strong>{Math.floor(quizTimer / 60)}:{(quizTimer % 60).toString().padStart(2, '0')}</strong>*/}


            {/*            <div className={`quiz-card ${animateNext ? "slide-out" : "slide-in"}`}>*/}

            {/*                <p className="text-gray-700 font-bold mb-2 text-lg">*/}
            {/*                    Question {currentQuestionIndex + 1} of {quizQuestions.length}*/}
            {/*                </p>*/}

            {/*                <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>*/}
            {/*                    {quizQuestions[currentQuestionIndex].question}*/}
            {/*                </ReactMarkdown>*/}

            {/*                {quizQuestions[currentQuestionIndex]?.options.map((option, i) => (*/}
            {/*                    <label*/}
            {/*                        key={i}*/}
            {/*                        className={`option ${answers[currentQuestionIndex] === option ? "selected" : ""}`}*/}
            {/*                        onClick={() => handleAnswerChange(currentQuestionIndex, option)}*/}
            {/*                    >*/}
            {/*                        {String.fromCharCode(65 + i)}. {option}*/}
            {/*                    </label>*/}
            {/*                ))}*/}


            {/*                <div className="mt-4 flex justify-between">*/}
            {/*                    <Button onClick={prevQuestion} disabled={currentQuestionIndex === 0}>*/}
            {/*                        Back*/}
            {/*                    </Button>*/}
            {/*                    {currentQuestionIndex === quizQuestions.length - 1 ? (*/}
            {/*                        <Button type="primary" onClick={handleSubmit} loading={isSubmitting}>*/}
            {/*                            Submit*/}
            {/*                        </Button>*/}
            {/*                    ) : (*/}
            {/*                        <Button type="primary" onClick={nextQuestion}>*/}
            {/*                            Next*/}
            {/*                        </Button>*/}
            {/*                    )}*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</Modal>*/}

            {/* Show Quiz Only When Started */}
            {/*{quizStarted && quizQuestions.length > 0 && (*/}

            {/*    <div className="quiz-container">*/}

            {/*        /!* Timer & Question Progress *!/*/}
            {/*        <div className="quiz-header">*/}
            {/*            <div className="question-progress">*/}
            {/*                {quizQuestions.map((_, index) => (*/}
            {/*                    <div*/}
            {/*                        key={index}*/}
            {/*                        className={`circle ${answers[index] !== undefined ? "attempted" : "unattempted"} ${index === currentQuestionIndex ? "current" : ""}`}*/}
            {/*                    >*/}
            {/*                        {index + 1}*/}
            {/*                    </div>*/}
            {/*                ))}*/}
            {/*            </div>*/}
            {/*            <div className="quiz-timer">*/}
            {/*                ⏳ {Math.floor(quizTimer / 60)}:{(quizTimer % 60).toString().padStart(2, '0')}*/}
            {/*            </div>*/}
            {/*        </div>*/}

            {/*        /!* Question Card *!/*/}
            {/*        <div className={`quiz-card ${animateNext ? "slide-out" : "slide-in"}`}>*/}
            {/*            <p className="question-text">*/}
            {/*                Question {currentQuestionIndex + 1}: {quizQuestions[currentQuestionIndex]?.question}*/}
            {/*            </p>*/}

            {/*            /!* Options with A, B, C, D Labels *!/*/}
            {/*            <div className="options-container">*/}
            {/*                {quizQuestions[currentQuestionIndex]?.options.map((option, i) => (*/}
            {/*                    <label*/}
            {/*                        key={i}*/}
            {/*                        className={`option ${answers[currentQuestionIndex] === option ? "selected" : ""}`}*/}
            {/*                        onClick={() => handleAnswerChange(currentQuestionIndex, option)}*/}
            {/*                    >*/}
            {/*                        {String.fromCharCode(65 + i)}. {option}*/}
            {/*                    </label>*/}
            {/*                ))}*/}
            {/*            </div>*/}
            {/*            /!* Navigation Buttons *!/*/}
            {/*            <div className="quiz-buttons">*/}
            {/*                <Button onClick={prevQuestion} disabled={currentQuestionIndex === 0}>*/}
            {/*                    Back*/}
            {/*                </Button>*/}
            {/*                {currentQuestionIndex === quizQuestions.length - 1 ? (*/}
            {/*                    <Button type="primary" onClick={handleSubmit} loading={isSubmitting}>*/}
            {/*                        Submit*/}
            {/*                    </Button>*/}
            {/*                ) : (*/}
            {/*                    <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black"*/}
            {/*                            onClick={nextQuestion}>*/}
            {/*                        Next*/}
            {/*                    </Button>*/}
            {/*                )}*/}
            {/*            </div>*/}


            {/*/!* Submit Button (Only on Last Question) *!/*/}
            {/*            /!*{currentQuestionIndex === quizQuestions.length - 1 && (*!/*/}
            {/*            /!*    <button className="submit-button" onClick={handleSubmit}>*!/*/}
            {/*            /!*        Submit*!/*/}
            {/*            /!*    </button>*!/*/}
            {/*            /!*)}*!/*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*)}*/}


            {/* Feedback Section with Score Details */}
            {/*{*/}
            {/*    scoreDetails.total_questions > 0 && (*/}
            {/*        <Card className="mt-6 p-6 shadow-xl border border-gray-300">*/}
            {/*            <h3 className="text-2xl font-bold font-gotham">Quiz Results</h3>*/}
            {/*            <p className="text-lg mt-2">✅ Correct: {scoreDetails.num_correct}</p>*/}
            {/*            <p className="text-lg">❌ Incorrect: {scoreDetails.num_incorrect}</p>*/}
            {/*            <p className="text-lg">📋 Total Questions: {scoreDetails.total_questions}</p>*/}
            {/*            <Progress percent={scoreDetails.score_percentage} status="active"/>*/}
            {/*        </Card>*/}
            {/*    )*/}
            {/*}*/}

            {/* Feedback Section */}
            {/*{*/}
            {/*    feedback.length > 0 && (*/}
            {/*        <div className="mt-6 p-6 bg-gray-100 rounded-lg">*/}
            {/*            <h3 className="text-lg font-semibold font-gotham mb-4">Quiz Feedback</h3>*/}
            {/*            {feedback.map((item, index) => (*/}
            {/*                <Card key={index} className="mb-4 p-4">*/}
            {/*                    <p className="font-bold font-gotham">{item.question}</p>*/}
            {/*                    <p className="text-gray-600">*/}
            {/*                        <strong>Your Answer:</strong> {item.selected_option}*/}
            {/*                    </p>*/}
            {/*                    <p className={`text-lg ${item.is_correct ? "text-green-600" : "text-red-600"}`}>*/}
            {/*                        {item.is_correct ? <CheckCircleOutlined/> : <CloseCircleOutlined/>}{" "}*/}
            {/*                        {item.is_correct ? "Correct" : "Incorrect"}*/}
            {/*                    </p>*/}
            {/*                    <p className="text-yellow-500">*/}
            {/*                        <strong>Correct Answer:</strong> {item.correct_answer}*/}
            {/*                    </p>*/}
            {/*                    <p className="text-gray-500">*/}
            {/*                        <strong>Explanation:</strong> {item.explanation}*/}
            {/*                    </p>*/}
            {/*                </Card>*/}
            {/*            ))}*/}
            {/*        </div>*/}
            {/*    )*/}
            {/*}*/}
            {/* Show options after quiz completion */}
            {/*    {quizEnded && (*/}
            {/*        <div className="quiz-end-container">*/}
            {/*            <h2 className="text-2xl font-bold font-gotham">Quiz Completed! 🎉</h2>*/}
            {/*            <p className="text-lg mt-2">Your score: {scoreDetails.score_percentage}%</p>*/}

            {/*            <div className="quiz-end-buttons">*/}
            {/*                /!* Retake the same quiz *!/*/}
            {/*                <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black"*/}
            {/*                        onClick={() => fetchQuiz(selectedTopic)}>*/}
            {/*                    Quiz Again - Current Topic*/}
            {/*                </Button>*/}

            {/*                /!* Take another topic *!/*/}
            {/*                <Button type="default" onClick={() => {*/}
            {/*                    setQuizEnded(false);*/}
            {/*                    setQuizStarted(false);*/}
            {/*                    setSelectedBook(null);*/}
            {/*                    setSelectedTopic(null);*/}
            {/*                    setQuizQuestions([]);*/}

            {/*                    // ✅ Clear previous results and feedback when switching topics*/}
            {/*                    setFeedback([]);*/}
            {/*                    setscoreDetails([]);*/}

            {/*                    // ✅ Reset the timer*/}
            {/*                    setQuizTimer(600);*/}
            {/*                }}>*/}
            {/*                    Take Quiz - Another Topic*/}
            {/*                </Button>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    )}*/}


            {/*</div>*/}
            <div className="min-h-screen font-[poppins] py-6 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Book and Topic Selection */}
                    <AnimatePresence mode="wait">
                        {!quizStarted && !quizEnded && (
                            <motion.div
                                key="selection"
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                variants={cardVariants}
                                className="space-y-8"
                            >
                                {/*<div className="flex items-center justify-center mb-6">*/}
                                {/*    <motion.div*/}
                                {/*        initial={{opacity: 0, scale: 0.8}}*/}
                                {/*        animate={{opacity: 1, scale: 1}}*/}
                                {/*        transition={{duration: 0.5}}*/}
                                {/*        className="bg-white px-8 py-4 rounded-full shadow-lg inline-flex items-center space-x-3"*/}
                                {/*    >*/}
                                {/*        <BookOpen className="h-7 w-7 text-[#D67C2C]"/>*/}
                                {/*        <h1 className="text-3xl font-bold text-gray-800">Quiz Selection</h1>*/}
                                {/*    </motion.div>*/}
                                {/*</div>*/}

                                {/* Header */}
                                <div
                                    className=" top-0 z-10 backdrop-blur-sm py-6 mb-6">
                                    <div className="flex items-center justify-center">

                                        <h1 className="text-4xl font-bold text-[#D67C2C] text-center">Quiz Selection</h1>

                                    </div>
                                </div>

                                <motion.div
                                    variants={cardVariants}
                                    className="bg-white p-8 mb-8"
                                >
                                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Select a Quiz Book</h2>

                                    <div
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {books.map((book, idx) => (
                                            <motion.div
                                                key={book.book_id}
                                                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                    scale: 1,
                                                    transition: { delay: idx * 0.1, duration: 0.5 }
                                                }}
                                                whileHover={{
                                                    y: -8,
                                                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                                                    borderColor: "#F17C45"
                                                }}
                                                className="bg-white border-2 border-[#D67C2C] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                                            >
                                                <div className="h-2 bg-[#D67C2C]"></div>
                                                <div className="p-6">
                                                    <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 min-h-[3.5rem]">{book.name}</h3>
                                                    {/*<div className="flex items-center text-gray-600 mb-4">*/}
                                                    {/*    <Clock className="h-4 w-4 mr-2"/>*/}
                                                    {/*    <span>{book.hours_to_complete} hours to complete</span>*/}
                                                    {/*</div>*/}
                                                    <button
                                                        onClick={() => {
                                                            setSelectedBook(book);
                                                            fetchTopics(book.book_id);
                                                        }}
                                                        className="w-full py-3 px-4 bg-[#D67C2C] text-white font-semibold rounded-lg hover:from-orange-400 hover:to-orange-500-700 transition-colors shadow-md hover:shadow-lg flex items-center justify-center"
                                                    >
                                                        <span>Select Book</span>
                                                        <ArrowRight className="h-4 w-4 ml-2" />
                                                    </button>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>

                                {selectedBook && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.5 }}
                                        className="bg-white rounded-2xl shadow-xl p-8"
                                    >
                                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Select a Topic</h2>

                                        {loading ? (
                                            <div className="flex justify-center items-center py-10">
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                                    className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full"
                                                ></motion.div>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {topics.map((topic, idx) => (
                                                    <motion.button
                                                        key={topic.topic_id}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                            transition: { delay: idx * 0.05, duration: 0.5 }
                                                        }}
                                                        whileHover={buttonHover}
                                                        onClick={() => fetchQuiz(topic.topic_id)}
                                                        className="p-4 text-left bg-gradient-to-r from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 border border-amber-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                                                    >
                                                        <div className="flex items-start">
                                                            <Zap
                                                                className="h-5 w-5 text-amber-500 mt-1 mr-2 flex-shrink-0" />
                                                            <span
                                                                className="font-medium text-gray-800">{topic.name}</span>
                                                        </div>
                                                    </motion.button>
                                                ))}
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </motion.div>
                        )}

                        {/* Quiz Interface */}
                        {quizStarted && quizQuestions.length > 0 && (
                            <motion.div
                                key="quiz"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                                className="max-w-4xl mx-auto"
                            >
                                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                                    {/* Quiz Header with Progress and Timer */}
                                    <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 text-white">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center space-x-2">
                                                <div className="flex items-center">
                                                    <Timer className="h-5 w-5 mr-2" />
                                                    <span
                                                        className="text-lg font-semibold">{formatTime(quizTimer)}</span>
                                                </div>

                                                {streak > 0 && (
                                                    <div
                                                        className="bg-white text-amber-600 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                                                        <Zap className="h-4 w-4 mr-1" />
                                                        <span>Streak: {streak}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="text-sm font-medium">
                                                Question {currentQuestionIndex + 1} of {quizQuestions.length}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="relative h-2 bg-gray-200">
                                        <motion.div
                                            initial={{ width: '0%' }}
                                            animate={{ width: `${getProgressPercentage()}%` }}
                                            transition={{ duration: 0.5 }}
                                            className="absolute h-full bg-gradient-to-r from-amber-500 to-amber-600"
                                        ></motion.div>
                                    </div>

                                    {/* Question Indicators */}
                                    <div className="overflow-x-auto px-4 py-3 scrollbar-hide">
                                        <div className="flex space-x-2 w-max">
                                            {quizQuestions.map((_, index) => (
                                                <motion.button
                                                    key={index}
                                                    ref={(el) => (questionRefs.current[index] = el)}
                                                    whileHover={{ scale: 1.1 }}
                                                    animate={{
                                                        scale: index === currentQuestionIndex ? 1.1 : 1,
                                                        backgroundColor:
                                                            isQuestionAnswered(index)
                                                                ? (index === currentQuestionIndex ? 'rgb(245, 158, 11)' : 'rgb(16, 185, 129)')
                                                                : (index === currentQuestionIndex ? 'rgb(245, 158, 11)' : 'rgb(229, 231, 235)')
                                                    }}
                                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-white shadow-sm transition-all duration-300`}
                                                    onClick={() => setCurrentQuestionIndex(index)}
                                                >
                                                    {isQuestionAnswered(index) ? (
                                                        <CircleCheck className="h-5 w-5" />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </motion.button>
                                            ))}
                                        </div>
                                    </div>


                                    {/* Question and Options */}
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={currentQuestionIndex}
                                            initial={{ opacity: 0, x: animateNext ? -50 : 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: animateNext ? 50 : -50 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 md:p-8"
                                        >
                                            <div className="bg-amber-50 rounded-xl p-6 mb-6 shadow-sm relative">
                                                <div
                                                    className="absolute -top-4 -left-4 bg-amber-500 text-white h-10 w-10 rounded-full flex items-center justify-center font-bold shadow-lg">
                                                    Q{currentQuestionIndex + 1}
                                                </div>
                                                <h2 className="text-xl font-semibold text-gray-800 ml-6">
                                                    {quizQuestions[currentQuestionIndex]?.question}
                                                </h2>
                                            </div>

                                            <div className="space-y-3">
                                                {(() => {
                                                    const currentQ = quizQuestions[currentQuestionIndex];
                                                    const qType = currentQ?.question_type || "mcq";
                                                    const isGrowth = quizFormat === "growth";

                                                    // Domain badge for growth questions
                                                    const domainBadge = isGrowth && currentQ?.domain ? (
                                                        <div className="flex gap-2 mb-3">
                                                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">{currentQ.domain}</span>
                                                            {currentQ.sub_metric && <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{currentQ.sub_metric.replace(/_/g, " ")}</span>}
                                                        </div>
                                                    ) : null;

                                                    // MCQ / scenario_mcq — standard options (must have options array or be explicitly MCQ type)
                                                    if (qType === "mcq" || qType === "scenario_mcq" || (!isGrowth && currentQ?.options?.length > 0)) {
                                                        return (
                                                            <>
                                                                {domainBadge}
                                                                {currentQ?.options?.map((option, i) => (
                                                                    <motion.button
                                                                        key={i}
                                                                        whileHover={{ scale: 1.01 }}
                                                                        whileTap={{ scale: 0.99 }}
                                                                        onClick={() => {
                                                                            handleAnswerChange(currentQuestionIndex, option);
                                                                            if (isGrowth) setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], selected_option: option } }));
                                                                        }}
                                                                        className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-300 flex items-center ${answers[currentQuestionIndex] === option
                                                                            ? "border-amber-500 bg-amber-50 shadow-md"
                                                                            : "border-gray-200 hover:border-amber-200 hover:bg-amber-50"
                                                                        }`}
                                                                    >
                                                                        <div className={`h-8 w-8 rounded-full mr-4 flex items-center justify-center font-semibold ${answers[currentQuestionIndex] === option ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-700"}`}>
                                                                            {String.fromCharCode(65 + i)}
                                                                        </div>
                                                                        <span className="text-gray-800">{option}</span>
                                                                    </motion.button>
                                                                ))}
                                                            </>
                                                        );
                                                    }

                                                    // True/False with explanation
                                                    if (qType === "true_false_explain") {
                                                        const ga = growthAnswers[currentQuestionIndex] || {};
                                                        return (
                                                            <>
                                                                {domainBadge}
                                                                <div className="flex gap-3 mb-4">
                                                                    {[true, false].map(val => (
                                                                        <button
                                                                            key={String(val)}
                                                                            onClick={() => setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], bool_answer: val } }))}
                                                                            className={`flex-1 p-4 rounded-xl border-2 font-semibold text-lg transition-all ${ga.bool_answer === val ? "border-amber-500 bg-amber-50 shadow-md" : "border-gray-200 hover:border-amber-200"}`}
                                                                        >
                                                                            {val ? "True" : "False"}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                <textarea
                                                                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 outline-none resize-y min-h-[80px] font-[poppins] text-sm"
                                                                    placeholder="Explain your answer..."
                                                                    value={ga.explanation || ""}
                                                                    onChange={e => setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], explanation: e.target.value } }))}
                                                                />
                                                            </>
                                                        );
                                                    }

                                                    // Open-ended / decision_tradeoff
                                                    if (qType === "open_ended" || qType === "decision_tradeoff") {
                                                        const ga = growthAnswers[currentQuestionIndex] || {};
                                                        return (
                                                            <>
                                                                {domainBadge}
                                                                <textarea
                                                                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-amber-500 outline-none resize-y min-h-[120px] font-[poppins] text-sm"
                                                                    placeholder="Write your detailed answer..."
                                                                    value={ga.text_answer || ""}
                                                                    onChange={e => setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], text_answer: e.target.value } }))}
                                                                />
                                                            </>
                                                        );
                                                    }

                                                    // Case-based (sub-questions)
                                                    if (qType === "case_based") {
                                                        const ga = growthAnswers[currentQuestionIndex] || {};
                                                        return (
                                                            <>
                                                                {domainBadge}
                                                                {currentQ.sub_questions && currentQ.sub_questions.map((sq, si) => (
                                                                    <div key={si} className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                                                        <p className="text-sm font-medium text-gray-700 mb-2">{si + 1}. {sq.question || sq.sub_question}</p>
                                                                        <textarea
                                                                            className="w-full p-3 border border-gray-200 rounded-lg focus:border-amber-500 outline-none resize-y min-h-[60px] font-[poppins] text-sm"
                                                                            placeholder="Your answer..."
                                                                            value={(ga[`sub_${si}`]) || ""}
                                                                            onChange={e => setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], [`sub_${si}`]: e.target.value, text_answer: Object.entries({ ...prev[currentQuestionIndex], [`sub_${si}`]: e.target.value }).filter(([k]) => k.startsWith("sub_")).map(([, v]) => v).join(" | ") } }))}
                                                                        />
                                                                    </div>
                                                                ))}
                                                                {!currentQ.sub_questions && (
                                                                    <textarea
                                                                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-amber-500 outline-none resize-y min-h-[120px] font-[poppins] text-sm"
                                                                        placeholder="Write your analysis..."
                                                                        value={ga.text_answer || ""}
                                                                        onChange={e => setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], text_answer: e.target.value } }))}
                                                                    />
                                                                )}
                                                            </>
                                                        );
                                                    }

                                                    // Fallback: if options exist render as MCQ, otherwise render a textarea for subjective answer
                                                    if (currentQ?.options?.length > 0) {
                                                        return currentQ.options.map((option, i) => (
                                                            <motion.button
                                                                key={i}
                                                                whileHover={{ scale: 1.01 }}
                                                                whileTap={{ scale: 0.99 }}
                                                                onClick={() => handleAnswerChange(currentQuestionIndex, option)}
                                                                className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-300 flex items-center ${answers[currentQuestionIndex] === option ? "border-amber-500 bg-amber-50 shadow-md" : "border-gray-200 hover:border-amber-200 hover:bg-amber-50"}`}
                                                            >
                                                                <div className={`h-8 w-8 rounded-full mr-4 flex items-center justify-center font-semibold ${answers[currentQuestionIndex] === option ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-700"}`}>
                                                                    {String.fromCharCode(65 + i)}
                                                                </div>
                                                                <span className="text-gray-800">{option}</span>
                                                            </motion.button>
                                                        ));
                                                    }
                                                    // Subjective / unrecognised type with no options — render a text area
                                                    const ga = growthAnswers[currentQuestionIndex] || {};
                                                    return (
                                                        <textarea
                                                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-amber-500 outline-none resize-y min-h-[120px] font-[poppins] text-sm"
                                                            placeholder="Write your answer..."
                                                            value={ga.text_answer || ""}
                                                            onChange={e => setGrowthAnswers(prev => ({ ...prev, [currentQuestionIndex]: { ...prev[currentQuestionIndex], text_answer: e.target.value } }))}
                                                        />
                                                    );
                                                })()}
                                            </div>

                                            {/* Navigation Buttons */}
                                            <div className="flex justify-between mt-8">
                                                <motion.button
                                                    whileHover={buttonHover}
                                                    onClick={prevQuestion}
                                                    disabled={currentQuestionIndex === 0}
                                                    className={`px-6 py-3 rounded-lg flex items-center ${currentQuestionIndex === 0
                                                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                            : "bg-white border border-amber-300 text-amber-600 hover:bg-amber-50"
                                                        }`}
                                                >
                                                    <ChevronLeft className="h-5 w-5 mr-1" />
                                                    Previous
                                                </motion.button>

                                                {currentQuestionIndex === quizQuestions.length - 1 ? (
                                                    <motion.button
                                                        whileHover={buttonHover}
                                                        onClick={handleSubmit}
                                                        disabled={isSubmitting}
                                                        className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg flex items-center shadow-md"
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <motion.div
                                                                    animate={{ rotate: 360 }}
                                                                    transition={{
                                                                        repeat: Infinity,
                                                                        duration: 1,
                                                                        ease: "linear"
                                                                    }}
                                                                    className="h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"
                                                                ></motion.div>
                                                                Submitting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                Submit Quiz
                                                                <CheckCircle className="h-5 w-5 ml-2" />
                                                            </>
                                                        )}
                                                    </motion.button>
                                                ) : (
                                                    <motion.button
                                                        whileHover={buttonHover}
                                                        onClick={nextQuestion}
                                                        className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg flex items-center shadow-md"
                                                    >
                                                        Next Question
                                                        <ChevronRight className="h-5 w-5 ml-1" />
                                                    </motion.button>
                                                )}
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                            </motion.div>
                        )}

                        {/* Quiz Results */}
                        {quizEnded && (
                            <motion.div
                                key="results"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                                className="max-w-4xl mx-auto space-y-6"
                            >
                                {quizFormat === "growth" ? (
                                    /* ── Growth quiz: pending teacher review ── */
                                    <motion.div
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ delay: 0.2, duration: 0.5 }}
                                        className="bg-white rounded-2xl shadow-xl overflow-hidden"
                                    >
                                        <div className="p-10 text-center">
                                            <div className="text-6xl mb-6">📬</div>
                                            <h2 className="text-3xl font-bold text-gray-800 mb-3">Quiz Submitted!</h2>
                                            <p className="text-gray-500 text-lg mb-2">
                                                Your answers have been saved successfully.
                                            </p>
                                            {pendingReviewCount > 0 && (
                                                <p className="text-gray-500 mb-8">
                                                    <span className="font-semibold text-[#D67C2C]">{pendingReviewCount}</span> question{pendingReviewCount > 1 ? "s require" : " requires"} teacher review. Your full results will be available once your teacher completes the assessment.
                                                </p>
                                            )}
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8 text-left">
                                                <p className="text-amber-800 text-sm font-medium">What happens next?</p>
                                                <ul className="mt-2 space-y-1 text-sm text-amber-700 list-disc list-inside">
                                                    <li>Your teacher will review and score your written answers</li>
                                                    <li>Your results and feedback will appear in your Growth Report</li>
                                                </ul>
                                            </div>
                                            <motion.button
                                                whileHover={buttonHover}
                                                onClick={() => {
                                                    setQuizEnded(false);
                                                    setQuizStarted(false);
                                                    setSelectedBook(null);
                                                    setSelectedTopic(null);
                                                    setQuizQuestions([]);
                                                    setFeedback([]);
                                                    setscoreDetails([]);
                                                    setQuizTimer(600);
                                                    setGrowthAnswers({});
                                                    setPendingReviewCount(0);
                                                }}
                                                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center mx-auto shadow-md font-medium"
                                            >
                                                <BookOpen className="h-5 w-5 mr-2" />
                                                Back to My Learnings
                                            </motion.button>
                                        </div>
                                    </motion.div>
                                ) : (
                                    /* ── Legacy MCQ quiz: instant results ── */
                                    <>
                                        <motion.div
                                            initial={{ scale: 0.9, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: 0.2, duration: 0.5 }}
                                            className="bg-white rounded-2xl shadow-xl overflow-hidden"
                                        >
                                            <div className="p-8 text-center">
                                                <motion.div
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                                                    className="text-5xl mb-4 flex justify-center"
                                                >
                                                    {getScoreEmoji(scoreDetails.score_percentage)}
                                                </motion.div>

                                                <h2 className="text-3xl font-bold text-gray-800 mb-6">Quiz Completed!</h2>

                                                <div className="flex justify-center mb-8">
                                                    <div className="bg-gray-100 rounded-full overflow-hidden w-full max-w-md h-4">
                                                        <motion.div
                                                            initial={{ width: 0 }}
                                                            animate={{ width: `${scoreDetails.score_percentage}%` }}
                                                            transition={{ delay: 0.6, duration: 1 }}
                                                            className={`h-full ${getScoreColor(scoreDetails.score_percentage)}`}
                                                        ></motion.div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                                                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                                                        <div className="text-green-600 font-medium">Correct</div>
                                                        <div className="text-3xl font-bold text-green-700">{scoreDetails.num_correct}</div>
                                                    </div>
                                                    <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                                                        <div className="text-red-600 font-medium">Incorrect</div>
                                                        <div className="text-3xl font-bold text-red-700">{scoreDetails.num_incorrect}</div>
                                                    </div>
                                                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                                        <div className="text-[#D67C2C] font-medium">Score</div>
                                                        <div className="text-3xl font-bold text-[#D67C2C]">{scoreDetails.score_percentage}%</div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col sm:flex-row justify-center gap-4">
                                                    <motion.button
                                                        whileHover={buttonHover}
                                                        onClick={() => fetchQuiz(selectedTopic)}
                                                        className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center shadow-md"
                                                    >
                                                        <RefreshCw className="h-5 w-5 mr-2" />
                                                        Try Again
                                                    </motion.button>
                                                    <motion.button
                                                        whileHover={buttonHover}
                                                        onClick={() => {
                                                            setQuizEnded(false);
                                                            setQuizStarted(false);
                                                            setSelectedBook(null);
                                                            setSelectedTopic(null);
                                                            setQuizQuestions([]);
                                                            setFeedback([]);
                                                            setscoreDetails([]);
                                                            setQuizTimer(600);
                                                        }}
                                                        className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg flex items-center justify-center"
                                                    >
                                                        <BookOpen className="h-5 w-5 mr-2" />
                                                        Choose Another Topic
                                                    </motion.button>
                                                </div>
                                            </div>
                                        </motion.div>

                                        {/* Detailed Feedback */}
                                        {feedback.length > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.4, duration: 0.5 }}
                                                className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
                                            >
                                                <h3 className="text-2xl font-bold text-gray-800 mb-6">Detailed Feedback</h3>
                                                <div className="space-y-6">
                                                    {feedback.map((item, index) => (
                                                        <motion.div
                                                            key={index}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                                                            className={`p-6 rounded-xl border-l-4 ${item.is_correct ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}
                                                        >
                                                            <div className="flex items-start">
                                                                <div className={`mt-0.5 mr-3 ${item.is_correct ? "text-green-500" : "text-red-500"}`}>
                                                                    {item.is_correct ? <CheckCircle className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h4 className="text-lg font-medium text-gray-800 mb-2">{item.question}</h4>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                                                                            <p className="text-sm text-gray-500 mb-1">Your Answer:</p>
                                                                            <p className={`font-medium ${item.is_correct ? "text-green-600" : "text-red-600"}`}>{item.selected_option}</p>
                                                                        </div>
                                                                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                                                                            <p className="text-sm text-gray-500 mb-1">Correct Answer:</p>
                                                                            <p className="font-medium text-green-600">{item.correct_answer}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                                        <p className="text-sm text-gray-500 mb-1">Explanation:</p>
                                                                        <p className="text-gray-700">{item.explanation}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </Layout>
    )
        ;
};

export default QuizPage;
